import dayjs from "dayjs";

export const fmtDate = (d, withTime = false) =>
    d ? dayjs(d).format(withTime ? "DD MMM YYYY, HH:mm" : "DD MMM YYYY") : "—";

export const fmtRange = (start, end) => {
    if (!start && !end) return "—";
    if (start && end) return `${fmtDate(start)} → ${fmtDate(end)}`;
    return fmtDate(start || end);
};

// Volunteer tier/rank derived from total points.
export const getTierStyleByPoints = (points) => {
    const p = Number(points) || 0;
    if (p < 100) return { color: "#B0BEC5", width: 2, icon: "seed-outline", label: "Seed" };
    if (p < 200) return { color: "#3bb911", width: 2, icon: "shield-star-outline", label: "Star" };
    if (p < 300) return { color: "#FBC02D", width: 2, icon: "trophy-outline", label: "Trophy" };
    if (p < 500) return { color: "#ff6600", width: 2, icon: "fire", label: "Fire" };
    return { color: "#E53935", width: 2, icon: "crown-outline", label: "Crown" };
};

/**
 * Downscale + re-encode an image in the browser so uploads stay small.
 * Returns a new JPEG File, or the original file when compression isn't
 * applicable (non-raster types, or already small enough).
 */
export async function compressImage(
    file,
    { maxDimension = 1600, quality = 0.8 } = {}
) {
    if (
        !file ||
        !file.type ||
        !file.type.startsWith("image/") ||
        file.type === "image/gif" ||
        file.type === "image/svg+xml"
    ) {
        return file;
    }

    const dataUrl = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });

    const img = await new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => resolve(image);
        image.onerror = reject;
        image.src = dataUrl;
    });

    const { width, height } = img;
    // Already small in both dimensions and bytes → keep as-is.
    if (
        width <= maxDimension &&
        height <= maxDimension &&
        file.size < 1.5 * 1024 * 1024
    ) {
        return file;
    }

    const scale = Math.min(1, maxDimension / Math.max(width, height));
    const w = Math.round(width * scale);
    const h = Math.round(height * scale);

    const canvas = document.createElement("canvas");
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(img, 0, 0, w, h);

    const blob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/jpeg", quality)
    );
    if (!blob) return file;

    const name = (file.name || "image").replace(/\.\w+$/, "") + ".jpg";
    return new File([blob], name, { type: "image/jpeg" });
}
